import React, { useMemo, useState } from "react";
import "./login.css";
import { PCB_CORE_URL } from "../../config";
import { useStore } from "../../store";
import { useNavigate } from "react-router-dom";
import { setAuthTokens } from "../../authSession";

const API_LOGIN = `${PCB_CORE_URL}/login/`;

export default function Login() {
  const setUser = useStore((s) => s.setUser);
  const navigate = useNavigate(); // ici (top-level)

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const canSubmit = useMemo(() => {
    return email.trim().length > 3 && password.trim().length >= 6 && !loading;
  }, [email, password, loading]);

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError("");
    setSuccessMsg("");
    if (!canSubmit) return;

    setLoading(true);
    try {
      const res = await fetch(API_LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          data?.detail ||
          data?.Message ||
          data?.message ||
          "Email ou mot de passe incorrect.";
        throw new Error(msg);
      }

      const u = data.user || {};
      const access = data.access || data.token || "";
      const refresh = data.refresh || "";

      setAuthTokens({ access, refresh });

      setUser({
        access,
        refresh,
        userId: String(u.id ?? data.userId ?? ""),
        full_name: u.full_name ?? data.full_name ?? "",
        email: u.email ?? email.trim(),
        is_staff: !!u.is_staff || !!data.is_staff,
        is_superuser: !!u.is_superuser || !!data.is_superuser,
      });

      setSuccessMsg("Connexion réussie.");

      const isAdmin =
        !!u.is_staff || !!u.is_superuser || !!data.is_superuser || !!data.is_staff;

      navigate(isAdmin ? "/bo" : "/app/projects", { replace: true });


    } catch (err) {
      setServerError(err.message || "Erreur lors de la connexion.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">Connexion</h1>
          <p className="auth-subtitle">Accédez à votre espace.</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <label className="auth-label">
            Email
            <input
              className="auth-input"
              type="email"
              autoComplete="email"
              placeholder="ex: nour@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
          </label>

          <label className="auth-label">
            Mot de passe
            <div className="pwd-row">
              <input
                className="auth-input"
                type={showPwd ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Minimum 6 caractères"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPwd((v) => !v)}
                disabled={loading}
              >
                {showPwd ? "Masquer" : "Voir"}
              </button>
            </div>
          </label>

          {serverError ? <div className="auth-alert error">{serverError}</div> : null}
          {successMsg ? <div className="auth-alert success">{successMsg}</div> : null}

          <button className="auth-btn" type="submit" disabled={!canSubmit}>
            {loading ? "Connexion..." : "Se connecter"}
          </button>

          <div className="auth-footer">
            <a className="auth-link" href="/forgot-password">
              Mot de passe oublié ?
            </a>
          </div>

          <div className="auth-footer">
            <span>Pas encore de compte ?</span>
            <a className="auth-link" href="/signup">
              Créer un compte
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
